import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { QueryKey } from '@/Config/api/queryKey';

export interface BulkSendRecipientResult {
  phone_number: string;
  success: boolean;
  error: string | null;
  conversation_id: string | null;
  is_existing_conversation: boolean;
}

export interface BulkSendTemplateResponse {
  total: number;
  succeeded: number;
  failed: number;
  results: BulkSendRecipientResult[];
}

export interface BulkSendTemplateRequest {
  phone_numbers: string[];
  template_name: string;
  language_code: string;
  components: { type: string; parameters?: Record<string, unknown>[] }[] | null;
  body_text: string;
}

export interface PhoneValidationResult {
  phone_number: string;
  normalized: string | null;
  valid: boolean;
  has_existing_conversation: boolean;
  existing_conversation_id: string | null;
}

export interface ValidatePhonesResponse {
  results: PhoneValidationResult[];
}

export interface ValidatePhonesRequest {
  phone_numbers: string[];
}

export const useValidatePhones = () =>
  useMutation<ValidatePhonesResponse, unknown, ValidatePhonesRequest>({
    mutationKey: [QueryKey.whatsappTemplates.validatePhones],
  });

export const useBulkSendWhatsAppTemplate = () => {
  const { t } = useTranslation('inbox_v2');
  const queryClient = useQueryClient();

  return useMutation<BulkSendTemplateResponse, unknown, BulkSendTemplateRequest>({
    mutationKey: [QueryKey.whatsappTemplates.sendBulk],
    onSuccess: (data) => {
      if (data.failed > 0 && data.succeeded === 0) {
        toast.error(t('bulk_send.toast_error'));
      } else if (data.failed > 0) {
        toast.warning(
          t('bulk_send.toast_partial', { succeeded: data.succeeded, failed: data.failed }),
        );
      } else {
        toast.success(t('bulk_send.toast_success'));
      }
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
    onError: () => {
      toast.error(t('bulk_send.toast_error'));
    },
  });
};
